import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { auroraTopbarSx } from '../theme/auroraSurface';
import {
  Box, Typography, Card, CardContent, Grid, Button, AppBar, Toolbar,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Alert, Chip, Menu, MenuItem, Avatar,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Drawer, List, ListItem, ListItemButton, ListItemIcon, ListItemText,
  FormControl, InputLabel, Select, InputBase, CircularProgress, Snackbar,
} from '@mui/material';
import {
  Add, Edit, Delete, Visibility, Search, Article, AccountCircle,
  Logout, OpenInNew, ContentCopy, Star, TrendingUp, MouseRounded, Web,
} from '@mui/icons-material';
import api from '../utils/api';
import { AuthContext } from '../context/AuthContext';
import LibraryCard from '../components/LibraryCard';
import { RADIUS } from '../theme/tokens';
import FeaturedLibrariesManagement from '../components/admin/FeaturedLibrariesManagement';
import AnalyticsPage from './admin/analytics/AnalyticsPage';
import PageBuilderManagement from '../components/admin/PageBuilderManagement';

// Tab descriptors for the SEO-relevant subset of AnalyticsPage
const SEO_ANALYTICS_TABS = [
  { value: 'acquisition', label: 'Acquisition', icon: <TrendingUp sx={{ fontSize: 16 }} /> },
  { value: 'behavior', label: 'Behavior', icon: <MouseRounded sx={{ fontSize: 16 }} /> },
];

const DRAWER_WIDTH = 240;

function SeoDashboard() {
  const { logout, user } = useContext(AuthContext);
  const navigate = useNavigate();

  // State
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 20, pages: 1 });
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPage, setEditingPage] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingPage, setDeletingPage] = useState(null);
  const [saving, setSaving] = useState(false);

  // Library search preview
  const [librarySearchQuery, setLibrarySearchQuery] = useState('');
  const [previewLibraries, setPreviewLibraries] = useState([]);
  const [searchingLibraries, setSearchingLibraries] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    location_name: '',
    slug: '',
    meta_title: '',
    meta_description: '',
    meta_keywords: '',
    page_content: '',
    status: 'draft',
    featured_city: '',
    featured_locality: '',
  });

  // User menu
  const [anchorEl, setAnchorEl] = useState(null);

  // Active view in the sidebar nav
  const [activeView, setActiveView] = useState('pages');

  // Fetch pages
  useEffect(() => {
    fetchPages();
  }, [pagination.page, searchQuery, statusFilter]);

  const fetchPages = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit,
      };
      if (searchQuery) params.search = searchQuery;
      if (statusFilter) params.status = statusFilter;

      const response = await api.get('/seo/pages', { params });
      setPages(response.data.data.pages);
      setPagination(prev => ({ ...prev, ...response.data.data.pagination }));
    } catch (err) {
      console.error('Error fetching SEO pages:', err);
      showSnackbar('Failed to fetch pages', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Search libraries preview
  useEffect(() => {
    if (!librarySearchQuery || librarySearchQuery.length < 2) {
      setPreviewLibraries([]);
      return;
    }
    const timer = setTimeout(() => {
      searchLibrariesPreview(librarySearchQuery);
    }, 500);
    return () => clearTimeout(timer);
  }, [librarySearchQuery]);

  const searchLibrariesPreview = async (q) => {
    try {
      setSearchingLibraries(true);
      const response = await api.get('/seo/search-libraries', { params: { q, limit: 12 } });
      setPreviewLibraries(response.data.data.libraries);
    } catch (err) {
      console.error('Error searching libraries:', err);
    } finally {
      setSearchingLibraries(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9\s]/g, '')
      .replace(/\s+/g, '_')
      .substring(0, 100);
  };

  const handleLocationNameChange = (e) => {
    const name = e.target.value;
    setFormData(prev => ({
      ...prev,
      location_name: name,
      slug: generateSlug(name),
    }));
    setLibrarySearchQuery(name);
  };

  const openCreateDialog = () => {
    setEditingPage(null);
    setFormData({
      location_name: '',
      slug: '',
      meta_title: '',
      meta_description: '',
      meta_keywords: '',
      page_content: '',
      status: 'draft',
      featured_city: '',
      featured_locality: '',
    });
    setLibrarySearchQuery('');
    setPreviewLibraries([]);
    setDialogOpen(true);
  };

  const openEditDialog = (page) => {
    setEditingPage(page);
    setFormData({
      location_name: page.location_name,
      slug: page.slug,
      meta_title: page.meta_title || '',
      meta_description: page.meta_description || '',
      meta_keywords: page.meta_keywords || '',
      page_content: page.page_content || '',
      status: page.status,
      featured_city: page.featured_city || '',
      featured_locality: page.featured_locality || '',
    });
    setLibrarySearchQuery(page.location_name);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.location_name.trim()) {
      showSnackbar('Location name is required', 'error');
      return;
    }

    try {
      setSaving(true);
      if (editingPage) {
        await api.put(`/seo/pages/${editingPage.id}`, formData);
        showSnackbar('Page updated successfully');
      } else {
        await api.post('/seo/pages', formData);
        showSnackbar('Page created successfully');
      }
      setDialogOpen(false);
      fetchPages();
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to save page';
      showSnackbar(msg, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingPage) return;
    try {
      await api.delete(`/seo/pages/${deletingPage.id}`);
      showSnackbar('Page deleted successfully');
      setDeleteDialogOpen(false);
      setDeletingPage(null);
      fetchPages();
    } catch (err) {
      showSnackbar('Failed to delete page', 'error');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const menuItems = [
    { id: 'pages', label: 'Pages', icon: <Article /> },
    { id: 'featured-libraries', label: 'Featured Libraries', icon: <Star /> },
    { id: 'analytics', label: 'Analytics', icon: <TrendingUp /> },
    { id: 'page-builder', label: 'Page Builder', icon: <Web /> },
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: 'transparent' }}>
      {/* AppBar */}
      <AppBar position="fixed" elevation={0} sx={(theme) => ({ ...auroraTopbarSx(theme), color: 'text.primary', zIndex: theme.zIndex.drawer + 1, borderBottom: 1, borderColor: 'divider' })}>
        <Toolbar>
          <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 700 }}>
            SEO Dashboard
          </Typography>
          <IconButton onClick={(e) => setAnchorEl(e.currentTarget)}>
            <AccountCircle />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={() => setAnchorEl(null)}
          >
            <MenuItem disabled>
              <Typography variant="body2">{user?.email}</Typography>
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <Logout fontSize="small" sx={{ mr: 1 }} />
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Box sx={{ display: 'flex', mt: 8 }}>
        {/* Sidebar */}
        <Drawer
          variant="permanent"
          sx={{
            width: DRAWER_WIDTH,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: DRAWER_WIDTH,
              boxSizing: 'border-box',
              top: 64,
              height: 'calc(100vh - 64px)',
              borderRight: '1px solid',
              borderColor: 'divider',
            },
          }}
        >
          <Box sx={{ p: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, px: 1 }}>
              MANAGEMENT
            </Typography>
            <List disablePadding>
              {menuItems.map((item) => (
                <ListItem key={item.id} disablePadding sx={{ mb: 0.5 }}>
                  <ListItemButton
                    selected={activeView === item.id}
                    onClick={() => setActiveView(item.id)}
                    sx={{
                      borderRadius: RADIUS.md,
                      '&.Mui-selected': {
                        bgcolor: 'primary.soft',
                        color: 'primary.main',
                        '&:hover': { bgcolor: 'primary.soft' },
                        '& .MuiListItemIcon-root': { color: 'primary.main' },
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText primary={item.label} />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Box>
        </Drawer>

        {/* Main Content */}
        <Box component="main" sx={{ flexGrow: 1, p: 3 }}>

          {/* ── Pages view ───────────────────────────────────────────────── */}
          {activeView === 'pages' && (
            <>
              {/* Header */}
              <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                <Typography variant="h5" fontWeight="700">
                  SEO Location Pages
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={openCreateDialog}
                  sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                >
                  Create Page
                </Button>
              </Box>

              {/* Filters */}
              <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Paper sx={{ flex: 1, minWidth: 200, display: 'flex', alignItems: 'center', px: 2, borderRadius: 2 }}>
                  <Search sx={{ color: 'text.secondary', mr: 1 }} />
                  <InputBase
                    fullWidth
                    placeholder="Search pages..."
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      setPagination(prev => ({ ...prev, page: 1 }));
                    }}
                    sx={{ py: 1 }}
                  />
                </Paper>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={statusFilter}
                    label="Status"
                    onChange={(e) => {
                      setStatusFilter(e.target.value);
                      setPagination(prev => ({ ...prev, page: 1 }));
                    }}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="draft">Draft</MenuItem>
                    <MenuItem value="published">Published</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              {/* Stats */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={4}>
                  <Card sx={{ borderRadius: 2 }}>
                    <CardContent>
                      <Typography variant="h4" fontWeight="700">{pagination.total}</Typography>
                      <Typography variant="body2" color="text.secondary">Total Pages</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Card sx={{ borderRadius: 2 }}>
                    <CardContent>
                      <Typography variant="h4" fontWeight="700" color="success.main">
                        {pages.filter(p => p.status === 'published').length}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">Published (this page)</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Card sx={{ borderRadius: 2 }}>
                    <CardContent>
                      <Typography variant="h4" fontWeight="700" color="warning.main">
                        {pages.filter(p => p.status === 'draft').length}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">Drafts (this page)</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Table */}
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                  <CircularProgress />
                </Box>
              ) : pages.length === 0 ? (
                <Paper sx={{ p: 6, textAlign: 'center', borderRadius: 2 }}>
                  <Article sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" gutterBottom>No SEO pages yet</Typography>
                  <Typography variant="body2" color="text.secondary" mb={2}>
                    Create your first SEO location page to start targeting local searches.
                  </Typography>
                  <Button variant="contained" startIcon={<Add />} onClick={openCreateDialog}>
                    Create Page
                  </Button>
                </Paper>
              ) : (
                <>
                  <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700 }}>Location</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Slug</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Meta Title</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Created</TableCell>
                          <TableCell sx={{ fontWeight: 700 }} align="right">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {pages.map((page) => (
                          <TableRow key={page.id} hover>
                            <TableCell>
                              <Typography fontWeight="600">{page.location_name}</Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={page.slug}
                                size="small"
                                variant="outlined"
                                sx={{ fontFamily: 'monospace' }}
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                                {page.meta_title || '-'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={page.status}
                                size="small"
                                color={page.status === 'published' ? 'success' : 'default'}
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="text.secondary">
                                {new Date(page.created_at).toLocaleDateString()}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              {page.status === 'published' && (
                                <IconButton
                                  size="small"
                                  onClick={() => window.open(`/library-in-${page.slug}`, '_blank')}
                                  title="View public page"
                                >
                                  <OpenInNew fontSize="small" />
                                </IconButton>
                              )}
                              <IconButton
                                size="small"
                                onClick={() => {
                                  navigator.clipboard.writeText(`/library-in-${page.slug}`);
                                  showSnackbar('URL copied to clipboard');
                                }}
                                title="Copy URL"
                              >
                                <ContentCopy fontSize="small" />
                              </IconButton>
                              <IconButton size="small" onClick={() => openEditDialog(page)} title="Edit">
                                <Edit fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => {
                                  setDeletingPage(page);
                                  setDeleteDialogOpen(true);
                                }}
                                title="Delete"
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {/* Pagination */}
                  {pagination.pages > 1 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 3 }}>
                      <Button
                        size="small"
                        disabled={pagination.page <= 1}
                        onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                      >
                        Previous
                      </Button>
                      <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                        Page {pagination.page} of {pagination.pages}
                      </Typography>
                      <Button
                        size="small"
                        disabled={pagination.page >= pagination.pages}
                        onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                      >
                        Next
                      </Button>
                    </Box>
                  )}
                </>
              )}
            </>
          )}

          {/* ── Featured Libraries view ──────────────────────────────────── */}
          {activeView === 'featured-libraries' && (
            <FeaturedLibrariesManagement apiBase="/seo" />
          )}

          {/* ── Analytics view ───────────────────────────────────────────── */}
          {activeView === 'analytics' && (
            <AnalyticsPage apiBase="/seo/analytics" tabs={SEO_ANALYTICS_TABS} />
          )}

          {/* ── Page Builder view ────────────────────────────────────────── */}
          {activeView === 'page-builder' && (
            <PageBuilderManagement apiBase="/seo/editable-pages" />
          )}

        </Box>
      </Box>

      {/* Create/Edit Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          {editingPage ? 'Edit SEO Page' : 'Create SEO Page'}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={3}>
            {/* Left Column - Form */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Location Name"
                value={formData.location_name}
                onChange={handleLocationNameChange}
                margin="normal"
                required
                helperText="e.g. Uttam Nagar, Dwarka, Rohini"
              />
              <TextField
                fullWidth
                label="Slug"
                value={formData.slug}
                onChange={(e) => setFormData(prev => ({ ...prev, slug: e.target.value }))}
                margin="normal"
                helperText={`URL: /library-in-${formData.slug || '...'}`}
                InputProps={{ sx: { fontFamily: 'monospace' } }}
              />
              <TextField
                fullWidth
                label="Meta Title"
                value={formData.meta_title}
                onChange={(e) => setFormData(prev => ({ ...prev, meta_title: e.target.value }))}
                margin="normal"
                helperText={`${(formData.meta_title || '').length}/500 characters`}
                inputProps={{ maxLength: 500 }}
              />
              <TextField
                fullWidth
                label="Meta Description"
                value={formData.meta_description}
                onChange={(e) => setFormData(prev => ({ ...prev, meta_description: e.target.value }))}
                margin="normal"
                multiline
                rows={3}
              />
              <TextField
                fullWidth
                label="Meta Keywords"
                value={formData.meta_keywords}
                onChange={(e) => setFormData(prev => ({ ...prev, meta_keywords: e.target.value }))}
                margin="normal"
                helperText="Comma-separated keywords"
              />
              <TextField
                fullWidth
                label="Page Content (HTML)"
                value={formData.page_content}
                onChange={(e) => setFormData(prev => ({ ...prev, page_content: e.target.value }))}
                margin="normal"
                multiline
                rows={8}
                InputProps={{ sx: { fontFamily: 'monospace', fontSize: '0.85rem' } }}
              />
              <FormControl fullWidth margin="normal">
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.status}
                  label="Status"
                  onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
                >
                  <MenuItem value="draft">Draft</MenuItem>
                  <MenuItem value="published">Published</MenuItem>
                </Select>
              </FormControl>
              <TextField
                fullWidth
                label="Featured City"
                value={formData.featured_city}
                onChange={(e) => setFormData(prev => ({ ...prev, featured_city: e.target.value }))}
                margin="normal"
                inputProps={{ maxLength: 120 }}
                helperText="Links this page to the curated featured-libraries set for this city (e.g. Delhi, Mumbai)."
              />
              <TextField
                fullWidth
                label="Featured Locality (optional)"
                value={formData.featured_locality}
                onChange={(e) => setFormData(prev => ({ ...prev, featured_locality: e.target.value }))}
                margin="normal"
                inputProps={{ maxLength: 100 }}
                helperText="Narrows the featured-libraries set to a specific locality within the city (e.g. Dwarka, Andheri)."
              />
            </Grid>

            {/* Right Column - Library Preview */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" fontWeight="700" gutterBottom sx={{ mt: 2 }}>
                Matching Libraries Preview
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Libraries that match the location name in their address fields.
              </Typography>

              {searchingLibraries ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress size={30} />
                </Box>
              ) : previewLibraries.length > 0 ? (
                <Box sx={{ maxHeight: 500, overflowY: 'auto', pr: 1 }}>
                  <Typography variant="body2" color="primary" fontWeight="600" mb={1}>
                    {previewLibraries.length} libraries found
                  </Typography>
                  {previewLibraries.map((lib) => (
                    <Card key={lib.id} variant="outlined" sx={{ mb: 1, borderRadius: 2 }}>
                      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                        <Typography variant="body2" fontWeight="600">{lib.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {lib.locality ? `${lib.locality}, ` : ''}{lib.city || lib.address}
                        </Typography>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              ) : formData.location_name.length >= 2 ? (
                <Paper sx={{ p: 3, textAlign: 'center', borderRadius: 2, bgcolor: 'action.hover' }}>
                  <Search sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    No libraries found matching "{formData.location_name}"
                  </Typography>
                </Paper>
              ) : (
                <Paper sx={{ p: 3, textAlign: 'center', borderRadius: 2, bgcolor: 'action.hover' }}>
                  <Search sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    Type a location name to preview matching libraries
                  </Typography>
                </Paper>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDialogOpen(false)} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || !formData.location_name.trim()}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {saving ? 'Saving...' : editingPage ? 'Update Page' : 'Create Page'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Delete SEO Page</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the page for <strong>{deletingPage?.location_name}</strong>?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDeleteDialogOpen(false)} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default SeoDashboard;
